import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { adminGet, adminPost, adminPatch, adminDelete } from "../services/api";
import {
  RssRegular, AddRegular, EditRegular, DeleteRegular, ArrowSyncRegular, BeakerRegular, DismissRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const Rss = fluentIcon(RssRegular);
const Plus = fluentIcon(AddRegular);
const Edit = fluentIcon(EditRegular);
const Trash = fluentIcon(DeleteRegular);
const Loader2 = fluentIcon(ArrowSyncRegular);
const Beaker = fluentIcon(BeakerRegular);
const X = fluentIcon(DismissRegular);

interface RssSourceRow {
  id: string;
  name: string;
  url: string;
  language: string;
  region: string | null;
  category: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lng: number | null;
  editorialIndependence: "independent" | "wire_service" | "state_affiliated" | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SourcesResponse {
  status: string;
  data: RssSourceRow[];
  total: number;
}

type FormState = {
  name: string;
  url: string;
  language: string;
  region: string;
  category: string;
  city: string;
  state: string;
  lat: string;
  lng: string;
  editorialIndependence: "" | "independent" | "wire_service" | "state_affiliated";
  isActive: boolean;
  notes: string;
};

const EMPTY_FORM: FormState = {
  name: "", url: "", language: "en", region: "", category: "",
  city: "", state: "", lat: "", lng: "", editorialIndependence: "", isActive: true, notes: "",
};

function formToPayload(f: FormState) {
  return {
    name: f.name.trim(),
    url: f.url.trim(),
    language: f.language.trim() || "en",
    region: f.region.trim() || undefined,
    category: f.category.trim() || undefined,
    city: f.city.trim() || undefined,
    state: f.state.trim() || undefined,
    lat: f.lat.trim() ? Number(f.lat) : undefined,
    lng: f.lng.trim() ? Number(f.lng) : undefined,
    editorialIndependence: f.editorialIndependence || undefined,
    isActive: f.isActive,
    notes: f.notes.trim() || undefined,
  };
}

const inputClass =
  "w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-brand-blue";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function SourceFormModal({
  initial,
  title,
  onSave,
  onClose,
  saving,
}: {
  initial: FormState;
  title: string;
  onSave: (payload: ReturnType<typeof formToPayload>) => void;
  onClose: () => void;
  saving: boolean;
}) {
  const { token } = useAuth();
  const [form, setForm] = useState<FormState>(initial);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const testFetch = useMutation({
    mutationFn: () =>
      adminPost<{ status: string; data: { count: number; sample: { title: string; source_url: string }[] } }>(
        "/api/v1/admin/sources/test-fetch",
        { url: form.url.trim(), name: form.name.trim() || "Test feed", language: form.language.trim() || "en" },
        token
      ),
  });

  const canSubmit = form.name.trim().length > 0 && form.url.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto">
      <div className="card w-full max-w-lg my-8">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-slate-100">{title}</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Name *">
            <input className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="NDTV Top Stories" />
          </Field>
          <Field label="RSS URL *">
            <input className={inputClass} value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://..." />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Language">
              <input className={inputClass} value={form.language} onChange={(e) => set("language", e.target.value)} placeholder="en" />
            </Field>
            <Field label="Region">
              <input className={inputClass} value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="IN" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <input className={inputClass} value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="top / business / sports…" />
            </Field>
            <Field label="Editorial independence">
              <select
                className={inputClass}
                value={form.editorialIndependence}
                onChange={(e) => set("editorialIndependence", e.target.value as FormState["editorialIndependence"])}
              >
                <option value="">—</option>
                <option value="independent">Independent</option>
                <option value="wire_service">Wire service</option>
                <option value="state_affiliated">State-affiliated</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="City (local-news feeds only)">
              <input className={inputClass} value={form.city} onChange={(e) => set("city", e.target.value)} />
            </Field>
            <Field label="State">
              <input className={inputClass} value={form.state} onChange={(e) => set("state", e.target.value)} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Lat">
              <input className={inputClass} value={form.lat} onChange={(e) => set("lat", e.target.value)} placeholder="28.6139" />
            </Field>
            <Field label="Lng">
              <input className={inputClass} value={form.lng} onChange={(e) => set("lng", e.target.value)} placeholder="77.2090" />
            </Field>
          </div>

          <Field label="Notes">
            <textarea className={`${inputClass} resize-none`} rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </Field>

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} />
            Active — included in the next ingestion run
          </label>

          <div className="pt-1">
            <button
              onClick={() => testFetch.mutate()}
              disabled={!form.url.trim() || testFetch.isPending}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-slate-900/60 border border-slate-800 text-slate-300 hover:border-brand-blue/40 hover:text-brand-blue disabled:opacity-40 transition-colors"
            >
              {testFetch.isPending ? <Loader2 size={14} className="animate-spin" /> : <Beaker size={14} />}
              Test fetch
            </button>
            {testFetch.isSuccess && (
              <div className="mt-2 text-xs bg-emerald-950/40 border border-emerald-900/40 rounded-lg p-2 text-emerald-300">
                Fetched {testFetch.data.data.count} item{testFetch.data.data.count === 1 ? "" : "s"}.
                {testFetch.data.data.sample.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-emerald-400/80">
                    {testFetch.data.data.sample.slice(0, 3).map((a, i) => (
                      <li key={i} className="truncate">• {a.title}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {testFetch.isError && (
              <div className="mt-2 text-xs bg-red-950/40 border border-red-900/40 rounded-lg p-2 text-red-300">
                {(testFetch.error as Error).message}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200">
            Cancel
          </button>
          <button
            onClick={() => onSave(formToPayload(form))}
            disabled={!canSubmit || saving}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-blue hover:bg-brand-blue/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// governance.md §6: write actions need a typed-confirmation modal, not a
// plain window.confirm() — same pattern as ToolsPage.tsx's ConfirmModal.
function DeleteConfirmModal({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim() === name;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="card w-full max-w-sm">
        <div className="text-sm font-semibold text-slate-100 mb-2">Delete source: {name}</div>
        <p className="text-xs text-slate-400 mb-4">
          This removes it from the next ingestion run. Type the source name to confirm.
        </p>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={name}
          className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 mb-4 focus:outline-none focus:border-brand-blue"
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches}
            className="px-3 py-2 rounded-lg text-sm font-medium bg-red-900/40 border border-red-900 text-red-300 hover:bg-red-900/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SourcesPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState<RssSourceRow | "new" | null>(null);
  const [deleting, setDeleting] = useState<RssSourceRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-sources"],
    queryFn: () => adminGet<SourcesResponse>("/api/v1/admin/sources", token),
    refetchInterval: 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-sources"] });

  const createMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof formToPayload>) => adminPost("/api/v1/admin/sources", payload, token),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<ReturnType<typeof formToPayload>> }) =>
      adminPatch(`/api/v1/admin/sources/${id}`, payload, token),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminPatch(`/api/v1/admin/sources/${id}`, { isActive }, token),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminDelete(`/api/v1/admin/sources/${id}`, token),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
    },
  });

  const rows = data?.data ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.url.toLowerCase().includes(q) ||
        (r.region ?? "").toLowerCase().includes(q) ||
        (r.category ?? "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const activeCount = rows.filter((r) => r.isActive).length;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">RSS Sources</h1>
          <p className="text-sm text-slate-500 mt-1">
            {rows.length} feeds ({activeCount} active) feeding the main ingestion pipeline. Changes take effect on
            the next cron tick — no deploy needed.
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-brand-blue hover:bg-brand-blue/80 transition-colors"
        >
          <Plus size={16} /> Add source
        </button>
      </div>

      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by name, URL, region, category…"
        className="w-full max-w-sm bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 mb-4 focus:outline-none focus:border-brand-blue"
      />

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading...
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-800">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">URL</th>
                <th className="px-4 py-3 font-medium">Lang</th>
                <th className="px-4 py-3 font-medium">Region / Category</th>
                <th className="px-4 py-3 font-medium">Editorial</th>
                <th className="px-4 py-3 font-medium">Active</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className={`border-b border-slate-800/60 last:border-0 ${r.isActive ? "" : "opacity-40"}`}
                >
                  <td className="px-4 py-3 text-slate-200 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-xs truncate" title={r.url}>
                    <a href={r.url} target="_blank" rel="noreferrer" className="hover:text-brand-blue">
                      {r.url}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{r.language}</td>
                  <td className="px-4 py-3 text-slate-400">
                    {[r.region, r.category].filter(Boolean).join(" / ") || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {r.editorialIndependence ? (
                      <span className="text-[10px] bg-slate-800 px-2 py-0.5 rounded text-slate-400">
                        {r.editorialIndependence.replace("_", " ")}
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMutation.mutate({ id: r.id, isActive: !r.isActive })}
                      disabled={toggleMutation.isPending}
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        r.isActive ? "bg-brand-blue" : "bg-slate-700"
                      }`}
                      aria-label={r.isActive ? "Deactivate source" : "Activate source"}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                          r.isActive ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => setEditing(r)} className="text-slate-500 hover:text-brand-blue">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => setDeleting(r)} className="text-slate-500 hover:text-red-400">
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    {rows.length === 0 ? (
                      <>No sources yet. <Rss size={14} className="inline mb-0.5" /> Add one to get started.</>
                    ) : (
                      "No sources match this filter."
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <SourceFormModal
          title={editing === "new" ? "Add source" : `Edit: ${editing.name}`}
          initial={
            editing === "new"
              ? EMPTY_FORM
              : {
                  name: editing.name,
                  url: editing.url,
                  language: editing.language,
                  region: editing.region ?? "",
                  category: editing.category ?? "",
                  city: editing.city ?? "",
                  state: editing.state ?? "",
                  lat: editing.lat != null ? String(editing.lat) : "",
                  lng: editing.lng != null ? String(editing.lng) : "",
                  editorialIndependence: editing.editorialIndependence ?? "",
                  isActive: editing.isActive,
                  notes: editing.notes ?? "",
                }
          }
          saving={createMutation.isPending || updateMutation.isPending}
          onClose={() => setEditing(null)}
          onSave={(payload) => {
            if (editing === "new") {
              createMutation.mutate(payload);
            } else {
              updateMutation.mutate({ id: editing.id, payload });
            }
          }}
        />
      )}

      {deleting && (
        <DeleteConfirmModal
          name={deleting.name}
          onCancel={() => setDeleting(null)}
          onConfirm={() => deleteMutation.mutate(deleting.id)}
        />
      )}
    </div>
  );
}
